/**
 * Platform Service — User Management API calls
 *
 * Phase 1 of SPC Decoupling: Core user CRUD calls now go to our local
 * Express server instead of Studio (VITE_API_BASE_URL).
 *
 * SPC-only functions (invitations, notifications) are stubbed out since
 * they relied on Studio. They return safe empty/no-op results so the UI
 * doesn't crash while we clean up the pages that still import them.
 *
 * Domain whitelisting still calls VITE_SERVICES_EXTERNAL_URL (separate service).
 */

import { authService as auth } from "../../utils/authService";
import { errorHandler } from "../../components/shared/errorhandler";
import { IInvitedUser } from "../../views/settings/platform/platform.interface";

// =============================================================================
// LOCAL ENDPOINTS — These now call our Express server directly
// =============================================================================

/**
 * GET /api/v1/admin/users — List all users
 * Was: VITE_API_BASE_URL + /api/security/getusers
 */
export const getUsersApi = async () => {
  return await fetch("/api/v1/admin/users", {
    headers: await auth.getAuthorizationHeader(),
  })
    .then(errorHandler)
    .then((response) => response.json());
};

/**
 * PUT /api/v1/admin/users/:email/block or /unblock
 * Was: VITE_API_BASE_URL + /api/security/blockunblockuser/:objectid?enabled=bool
 *
 * Note: Studio used objectid (B2C ID). We now use email for direct lookup.
 * The rowData.objectid field maps to the user's email in our local system.
 */
export const blockUnblockUserApi = async (rowData: { objectid: string }, isActive: boolean) => {
  const action = isActive ? "unblock" : "block";
  // rowData.objectid is the B2C object ID from the old API. Our local endpoint uses email.
  // The frontend will be updated later. For now, we pass it and the backend resolves it.
  return await fetch(`/api/v1/admin/users/${encodeURIComponent(rowData.objectid)}/${action}`, {
    method: "PUT",
    headers: await auth.getAuthorizationHeader(),
  })
    .then(errorHandler)
    .then((response) => response.json());
};

/**
 * POST /api/v1/admin/users — Create a new user
 * Was: VITE_API_BASE_URL + /api/security/ POST
 */
export const createUserApi = async (userModel: Record<string, unknown>) => {
  return await fetch("/api/v1/admin/users", {
    method: "POST",
    headers: await auth.getHeader(),
    body: JSON.stringify(userModel),
  })
    .then(errorHandler)
    .then((response) => response.json());
};

/**
 * POST /api/v1/admin/users/delete — Bulk delete users
 * Was: VITE_API_BASE_URL + /api/security/delete
 */
export const deleteUsersApi = async (userObjectIds: string[]) => {
  return await fetch("/api/v1/admin/users/delete", {
    method: "POST",
    headers: await auth.getHeader(),
    body: JSON.stringify({ objectIds: userObjectIds }),
  })
    .then(errorHandler)
    .then((response) => response.json());
};

/**
 * GET /api/v1/admin/users/search — Search users by email or name
 * Was: VITE_API_BASE_URL + /api/security/searchtenantusers
 */
export const getUserForSearchApi = async (search: string) => {
  return await fetch(`/api/v1/admin/users/search?search=${encodeURIComponent(search)}`, {
    headers: await auth.getAuthorizationHeader(),
  })
    .then(errorHandler)
    .then((response) => response.json());
};

/**
 * POST /api/v1/admin/users/check — Check if email already exists
 * Was: VITE_API_BASE_URL + /api/security/isuseradded
 */
export const IsUserAddedApi = async (payload: { email: string }) => {
  return await fetch("/api/v1/admin/users/check", {
    method: "POST",
    headers: await auth.getHeader(),
    body: JSON.stringify(payload),
  })
    .then(errorHandler)
    .then((response) => response.json());
};

// =============================================================================
// STUBBED SPC-ONLY FUNCTIONS
// These relied on Studio and no longer work. They return safe empty results
// so that pages importing them don't crash. Will be removed when those pages
// are cleaned up.
// =============================================================================

/** @deprecated SPC-only — returns empty array. Will be removed. */
export const getInvitedUsersApi = async (
  _pageNumber: number,
  _filter: string,
  _sortBy: string,
  _sortOrder: string,
) => {
  return [] as IInvitedUser[];
};

/** @deprecated SPC-only — returns zero count. Will be removed. */
export const getInvitedUserCountApi = async (
  _pageNumber: number,
  _filter: string,
  _sortBy: string,
  _sortOrder: string,
) => {
  return { isError: false, result: 0 };
};

/**
 * Get roles — now calls our local RBAC endpoint instead of Studio.
 * Was: VITE_API_BASE_URL + /api/security/
 */
export const getRolesApi = async () => {
  return await fetch("/api/v1/admin/rbac/roles", {
    headers: await auth.getAuthorizationHeader(),
  })
    .then(errorHandler)
    .then((response) => response.json())
    .then((data) => {
      // Transform local RBAC roles to the shape UsersPage expects (IRolesResponse)
      // The endpoint returns { roles: [...] }, so unwrap first
      const rawRoles = Array.isArray(data) ? data : Array.isArray(data?.roles) ? data.roles : [];
      const roles = rawRoles.map(
        (r: { id: string; label: string; description?: string; isHidden?: boolean }) => ({
          id: r.id,
          name: r.label || r.id,
          description: r.description || "",
          type: "custom",
          count: 0,
          isHidden: r.isHidden || false,
        }),
      );
      return { roles, isTenantAdmin: true };
    });
};

/** @deprecated SPC-only — no-op. Will be removed. */
export const addUserToInstanceApi = async (_addUserModel: object) => {
  return { isSuccess: false, message: "User instance assignment is managed via RBAC roles" };
};

/** @deprecated SPC-only — no-op. Will be removed. */
export const inviteUsersApi = async (_model: object) => {
  return { isSuccess: false, message: "Invitations are not available in this version" };
};

/** @deprecated SPC-only — no-op. Will be removed. */
export const unlinkUserRoleApi = async (_userObjectIds: string[]) => {
  return { isSuccess: false, message: "Role unlinking is managed via RBAC settings" };
};

/** @deprecated SPC-only — no-op. Will be removed. */
export const reInviteUserApi = async (_inviteUser: IInvitedUser) => {
  return { isSuccessful: false, message: "Invitations are not available in this version" };
};

/** @deprecated SPC-only — no-op. Will be removed. */
export const getNotificationsPage = async (_objectId: string) => {
  return {};
};

/** @deprecated SPC-only — no-op. Will be removed. */
export const isNotificationsSave = async (_payload: Record<string, unknown>, _objectId: string) => {
  return {};
};

// =============================================================================
// DOMAIN WHITELISTING — Still calls external service (not Studio)
// These use VITE_SERVICES_EXTERNAL_URL, which is a separate platform service.
// =============================================================================

export const addDomainWhitelistApi = async (domainData: { domain: string; name: string }) => {
  const urlParams = new URLSearchParams(window.location.search);
  const instanceId = urlParams.get("instanceid");
  return await fetch(
    import.meta.env.VITE_SERVICES_EXTERNAL_URL + `/instances/${instanceId}/domainwhitelists`,
    {
      method: "POST",
      headers: await auth.getAuthorizationHeader(),
      body: JSON.stringify(domainData),
    },
  )
    .then(errorHandler)
    .then((response) => response.json());
};

export const getDomainListApi = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const instanceId = urlParams.get("instanceid");
  return await fetch(
    import.meta.env.VITE_SERVICES_EXTERNAL_URL + `/instances/${instanceId}/domainwhitelists`,
    {
      method: "GET",
      headers: await auth.getAuthorizationHeader(),
    },
  )
    .then(errorHandler)
    .then((response) => response.json());
};

export const deleteDomainsApi = async (domainIds: string[]) => {
  const urlParams = new URLSearchParams(window.location.search);
  const instanceId = urlParams.get("instanceid");
  return await fetch(
    import.meta.env.VITE_SERVICES_EXTERNAL_URL + `/instances/${instanceId}/domainwhitelists`,
    {
      method: "DELETE",
      headers: await auth.getAuthorizationHeader(),
      body: JSON.stringify({ ids: domainIds }),
    },
  )
    .then(errorHandler)
    .then((response) => response.json());
};
